import { useEffect, useRef, useState } from "react";
import { LoaderCircle, Search } from "lucide-react";
import {
  AddressSearchController,
  type AddressSearchProvider,
  type AddressSuggestion,
} from "../../shared/address-search";
import { apiRequest, jsonBody, queryString } from "../lib/api";
import { Notice } from "./ui";

type AddressSearchResponse = {
  mode: "LOCAL_MOCK" | "LOCAL_REAL_ADDRESS_SEARCH" | "REAL_ADDRESS_SEARCH";
  provider?: "GEOAPIFY" | "LOCATIONIQ" | null;
  attribution?: string | null;
  reused?: boolean;
  items: AddressSuggestion[];
};

const CUSTOMER_PROVIDER_CODE: Record<NonNullable<AddressSearchResponse["provider"]>, "G" | "L"> = {
  GEOAPIFY: "G",
  LOCATIONIQ: "L",
};

class CheckoutAddressSearchProvider implements AddressSearchProvider {
  readonly id = "CHECKOUT_API" as const;
  readonly ready = true;

  constructor(
    private readonly checkoutId: string,
    private readonly branchId: string,
    private readonly onMetadata: (metadata: Omit<AddressSearchResponse, "items">) => void,
  ) {}

  async search(query: string, signal: AbortSignal): Promise<AddressSuggestion[]> {
    const local = ["localhost", "127.0.0.1", "::1"].includes(
      window.location.hostname.toLowerCase(),
    );
    const response = await apiRequest<AddressSearchResponse>(
      local
        ? `/api/shipping/address-search${queryString({
            checkoutId: this.checkoutId,
            q: query,
          })}`
        : "/api/shipping/address-search",
      local
        ? { signal }
        : {
            method: "POST",
            signal,
            ...jsonBody({
              checkoutId: this.checkoutId,
              branchId: this.branchId,
              q: query,
            }),
          },
    );
    if (!signal.aborted) this.onMetadata({
      mode: response.mode,
      provider: response.provider,
      attribution: response.attribution,
      reused: response.reused,
    });
    return response.items;
  }

  resolve(
    suggestion: AddressSuggestion,
    signal: AbortSignal,
  ): Promise<AddressSuggestion> {
    if (signal.aborted) {
      return Promise.reject(new DOMException("Pencarian dibatalkan.", "AbortError"));
    }
    if (!suggestion.point) {
      return Promise.reject(new Error("Koordinat alamat belum tersedia."));
    }
    return Promise.resolve({
      ...suggestion,
      point: { ...suggestion.point },
    });
  }
}

export function CheckoutAddressSearch({
  checkoutId,
  branchId,
  value,
  selected,
  error,
  onTextChange,
  onSelect,
  onLoadingChange,
}: {
  checkoutId: string;
  branchId: string;
  value: string;
  selected?: AddressSuggestion;
  error?: string;
  onTextChange: (value: string) => void;
  onSelect: (suggestion: AddressSuggestion) => void;
  onLoadingChange?: (loading: boolean) => void;
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [searchError, setSearchError] = useState<string>();
  const [searching, setSearching] = useState(false);
  const [reused, setReused] = useState(false);
  const [searchMetadata, setSearchMetadata] = useState<
    Omit<AddressSearchResponse, "items"> | undefined
  >();
  const controller = useRef<AddressSearchController | undefined>(undefined);

  useEffect(() => {
    const provider = new CheckoutAddressSearchProvider(
      checkoutId,
      branchId,
      setSearchMetadata,
    );
    const next = new AddressSearchController(
      provider,
      setSuggestions,
      () => undefined,
      setSearchError,
      450,
      3,
      {
        reuseMs: 30_000,
        onLoading: (loading) => {
          setSearching(loading);
          onLoadingChange?.(loading);
        },
        onReuse: setReused,
      },
    );
    controller.current = next;
    return () => {
      next.dispose();
      controller.current = undefined;
    };
  }, [branchId, checkoutId, onLoadingChange]);

  return (
    <div className="checkout-address-search" aria-busy={searching}>
      <label className="checkout-delivery-address-label">
        Patokan untuk ongkir
        <div className="origin-search-input">
          {searching ? <LoaderCircle aria-hidden="true" className="spin" /> : <Search aria-hidden="true" />}
          <input
            autoComplete="street-address"
            value={value}
            minLength={3}
            maxLength={200}
            placeholder="Contoh: Dalung Permai atau Jl. Sedap Malam"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "address-error" : "address-search-help"}
            onChange={(event) => {
              const next = event.target.value;
              onTextChange(next);
              if (selected?.point && next.trim() === value.trim()) {
                setReused(true);
                return;
              }
              setSuggestions([]);
              setSearchError(undefined);
              setSearchMetadata(undefined);
              setReused(false);
              controller.current?.input(next);
            }}
          />
        </div>
      </label>
      <small id="address-search-help">
        {searchMetadata?.mode === "LOCAL_REAL_ADDRESS_SEARCH" ||
        searchMetadata?.mode === "REAL_ADDRESS_SEARCH"
          ? "Pilih hasil alamat yang paling dekat dengan rumah."
          : searchMetadata?.mode === "LOCAL_MOCK"
            ? "LOCAL memakai data MOCK. Pilih hasil alamat yang paling sesuai."
            : "Ketik minimal 3 karakter, lalu pilih hasil alamat yang paling sesuai."}
      </small>
      {searching && <Notice tone="info">Mencari alamat… Mohon tunggu hasilnya.</Notice>}
      {!searching && (reused || searchMetadata?.reused) && (
        <small role="status">Alamat sama. Hasil sebelumnya digunakan kembali.</small>
      )}
      {(searchMetadata?.mode === "LOCAL_REAL_ADDRESS_SEARCH" ||
        searchMetadata?.mode === "REAL_ADDRESS_SEARCH") && searchMetadata.provider && (
        <small className="checkout-address-source" title="Kode sumber pencarian alamat">
          <strong>{CUSTOMER_PROVIDER_CODE[searchMetadata.provider]}</strong>
        </small>
      )}
      {searchError && <Notice tone="danger">{searchError}</Notice>}
      {suggestions.length > 0 && (
        <div className="origin-suggestions" role="listbox" aria-label="Pilihan alamat pengiriman">
          {suggestions.map((item) => (
            <button
              type="button"
              role="option"
              aria-selected={selected?.id === item.id}
              className={selected?.id === item.id ? "is-selected" : ""}
              key={item.id}
              onClick={() => {
                const signal = new AbortController().signal;
                void new CheckoutAddressSearchProvider(
                  checkoutId,
                  branchId,
                  setSearchMetadata,
                )
                  .resolve(item, signal)
                  .then((resolved) => {
                    onSelect(resolved);
                    controller.current?.reset();
                    setSuggestions([]);
                    setSearchError(undefined);
                  })
                  .catch((caught: unknown) => {
                    setSearchError(caught instanceof Error
                      ? caught.message
                      : "Alamat belum dapat dipilih.");
                  });
              }}
            >
              <strong>{item.displayName}</strong>
              <small>{item.secondaryText}</small>
            </button>
          ))}
        </div>
      )}
      {selected?.point && (
        <Notice tone="info">
          Patokan ongkir dipilih: {selected.displayName}. Jarak akan dihitung
          otomatis dari lokasi ini.
        </Notice>
      )}
      {error && (
        <small className="field-error" id="address-error">
          {error}
        </small>
      )}
    </div>
  );
}
